import React from 'react';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import type { InvestmentSummary } from '../types';
import { getPriceColor, formatMoney, formatPct, formatNumber } from '../utils/format';

interface SummaryCardsProps {
  summary: InvestmentSummary;
}

/**
 * 投资总结概览卡片
 * 总盈亏 / 胜率 / 交易次数 / 平均持仓天数
 */
export default function SummaryCards({ summary }: SummaryCardsProps) {
  return (
    <Grid container spacing={2}>
      {/* 总盈亏 */}
      <Grid item xs={2.4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {summary.totalProfit >= 0 ? (
                <TrendingUpIcon sx={{ color: 'error.main' }} />
              ) : (
                <TrendingDownIcon sx={{ color: 'success.main' }} />
              )}
              <Typography variant="body2" color="text.secondary">
                总盈亏
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: getPriceColor(summary.totalProfit) }}>
              {formatMoney(summary.totalProfit)}
            </Typography>
            <Typography variant="body2" sx={{ color: getPriceColor(summary.totalProfitPct) }}>
              {formatPct(summary.totalProfitPct)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* 胜率 */}
      <Grid item xs={2.4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ShowChartIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                胜率
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {formatNumber(summary.winRate, 1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {summary.tradeCount} 笔交易
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* 交易次数 */}
      <Grid item xs={2.4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ShowChartIcon color="info" />
              <Typography variant="body2" color="text.secondary">
                交易次数
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {summary.tradeCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              盈亏比: {summary.profitFactor >= 999 ? '∞' : summary.profitFactor.toFixed(2)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* 累计交易费用 */}
      <Grid item xs={2.4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AttachMoneyIcon color="warning" />
              <Typography variant="body2" color="text.secondary">
                累计交易费用
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
              {formatMoney(summary.totalFee ?? 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              占总盈亏: {summary.totalProfit !== 0
                ? formatNumber(Math.abs((summary.totalFee ?? 0) / summary.totalProfit) * 100, 1)
                : '0.0'}%
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* 平均持仓天数 */}
      <Grid item xs={2.4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ScheduleIcon color="action" />
              <Typography variant="body2" color="text.secondary">
                平均持仓天数
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {formatNumber(summary.avgHoldingDays, 1)} 天
            </Typography>
            <Typography variant="body2" color="text.secondary">
              最大盈利: {formatMoney(summary.maxProfit)} / 最大亏损: {formatMoney(summary.maxLoss)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
